import React, { useCallback, useState } from 'react';

import { Button as AntdButton, Modal as AntdModal } from 'antd';

import { UserDto, UserDtoProfileEnum } from 'lib_api/lib/api/gen';

import { Form } from 'components/WrappedComponents';
import { useUpdateUserStore, useUserStore } from 'hooks/UserStoreContext';

import CheckboxActif from './Fields/CheckboxActif';
import CheckboxGestionnaireLocal from './Fields/CheckboxGestionnaireLocal';
import InputEmail from './Fields/InputEmail';
import InputFirstName from './Fields/InputFirstName';
import InputLastName from './Fields/InputLastName';
import SelectUniteRattachement from './Fields/SelectUniteRattachement';
import {
  generateUpdateUserRequestDtoFromFormData,
  useSubmitUpdateUser,
  useValidateUpdateUser,
} from './utils';

interface ModalModificationProps {
  close: () => void;
  userToUpdate: UserDto;
  callAfterSubmit: () => void;
}

export interface ModificationUtilisateurFormValues {
  uniteRattachement?: string;
  lastName?: string;
  firstName?: string;
  actif?: boolean;
  email?: string;
  gestionnaireLocal?: boolean;
}

const getUserUniteRattachementId = (user: UserDto): string | undefined => {
  switch (user.profile) {
    case UserDtoProfileEnum.GARDIEN_FOURRIERE:
      return user.idCorrelationFourriere ?? undefined;
    case UserDtoProfileEnum.AUTORITE_FOURRIERE:
      return user.idCorrelationAutoriteFourriere ?? undefined;
    case UserDtoProfileEnum.FORCE_DE_L_ORDRE:
      return user.idCorrelationUniteFO ?? undefined;
    default:
      return undefined;
  }
};

const ModalModification = ({
  close,
  userToUpdate,
  callAfterSubmit,
}: ModalModificationProps): React.ReactElement => {
  const userId = userToUpdate.id;
  const [formData, setFormData] = useState<ModificationUtilisateurFormValues>({
    uniteRattachement: getUserUniteRattachementId(userToUpdate),
    lastName: userToUpdate.lastName ?? undefined,
    firstName: userToUpdate.firstName ?? undefined,
    actif: userToUpdate.enabled ?? undefined,
    email: userToUpdate.email ?? undefined,
    gestionnaireLocal: userToUpdate.gestionnaireLocal ?? undefined,
  });

  const activeUser = useUserStore();
  const updateUser: (user: UserDto) => void = useUpdateUserStore();

  const updateUserIfSameUserAsActive = (
    updatedUser: UserDto,
    activeUser: UserDto,
  ) => {
    if (updatedUser.id === activeUser.id) {
      updateUser(updatedUser);
    }
  };

  const [submitForm, thenSubmmit, catchSubmit] = useSubmitUpdateUser(
    (updatedUser: UserDto) => {
      close();
      updateUserIfSameUserAsActive(updatedUser, activeUser);
      callAfterSubmit();
    },
  );

  const memoSubmit = useCallback(async () => {
    return submitForm(formData, userId);
  }, [formData, submitForm, userId]);

  const [validateAndSubmit] = useValidateUpdateUser(
    memoSubmit,
    thenSubmmit,
    catchSubmit,
  );

  const isUpdatedUserSameAsActive = userId === activeUser.id;
  const isActiveUserProfileAdmin =
    activeUser.profile === UserDtoProfileEnum.ADMIN_FONCTIONNEL;
  const checkboxGestionnaireLocalEnabled = !!formData.uniteRattachement;

  return (
    <>
      <AntdModal
        open={true}
        onCancel={() => close()}
        footer={null}
        closable={false}
        destroyOnClose={true}
        width={680}
        maskClosable={false}
      >
        <Form
          initialValue={formData}
          id={'ModificationComptesForm'}
          onSubmit={() => {
            return validateAndSubmit(cuController =>
              cuController.validateUpdateUsingPOST(
                userId,
                generateUpdateUserRequestDtoFromFormData(formData),
              ),
            );
          }}
          onChange={values => {
            setFormData(values);
          }}
          validateButtonTitle={'Mettre à jour'}
          validateButtonClassName={'ValidateFormButton'}
        >
          {(values, SubmitButton): React.ReactElement => {
            return (
              <>
                <InputFirstName values={values} userId={userId} />
                <InputLastName values={values} userId={userId} />
                <InputEmail values={values} userId={userId} />
                <CheckboxActif
                  values={values}
                  userId={userId}
                  disabled={isUpdatedUserSameAsActive}
                />
                <SelectUniteRattachement
                  values={values}
                  userToUpdate={userToUpdate}
                  disabled={!isActiveUserProfileAdmin}
                />
                {checkboxGestionnaireLocalEnabled && (
                  <CheckboxGestionnaireLocal values={values} />
                )}
                <div className="ModalFooter">
                  <AntdButton onClick={close}>Annuler</AntdButton>
                  {SubmitButton}
                </div>
              </>
            );
          }}
        </Form>
      </AntdModal>
    </>
  );
};

export default ModalModification;
